/**
 * ScrapeKing Design System v2 - Badge Components
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

type BadgeVariant = 'default' | 'cyan' | 'green' | 'amber' | 'red' | 'purple';
type BadgeSize = 'sm' | 'md' | 'lg';

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const variantStyles = {
  default: css`
    background: var(--sk-surface-default);
    color: var(--sk-text-secondary);
    border-color: var(--sk-surface-border);
  `,
  cyan: css`
    background: rgba(0, 200, 224, 0.1);
    color: var(--sk-cyan-400);
    border-color: var(--sk-cyan-700);
  `,
  green: css`
    background: rgba(0, 255, 127, 0.1);
    color: var(--sk-green-400);
    border-color: var(--sk-green-700);
  `,
  amber: css`
    background: rgba(255, 184, 0, 0.1);
    color: var(--sk-amber-400);
    border-color: var(--sk-amber-700);
  `,
  red: css`
    background: rgba(255, 0, 51, 0.1);
    color: var(--sk-red-400);
    border-color: var(--sk-red-700);
  `,
  purple: css`
    background: rgba(139, 92, 246, 0.1);
    color: #a78bfa;
    border-color: rgba(139, 92, 246, 0.5);
  `,
};

const sizeStyles = {
  sm: css`
    padding: 2px var(--sk-space-2);
    font-size: var(--sk-text-2xs);
  `,
  md: css`
    padding: var(--sk-space-1) var(--sk-space-3);
    font-size: var(--sk-text-xs);
  `,
  lg: css`
    padding: var(--sk-space-2) var(--sk-space-4);
    font-size: var(--sk-text-sm);
  `,
};

const StyledBadge = styled.span<{
  $variant: BadgeVariant;
  $size: BadgeSize;
  $animated?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-1);
  font-family: var(--sk-font-mono);
  font-weight: 500;
  border-radius: var(--sk-radius-full);
  border: 1px solid;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;

  ${props => variantStyles[props.$variant]}
  ${props => sizeStyles[props.$size]}

  ${props => props.$animated && css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
`;

const BadgeDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  showDot?: boolean;
  animated?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export const Badge: FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  showDot = false,
  animated = false,
  style,
  className,
}) => {
  return (
    <StyledBadge $variant={variant} $size={size} $animated={animated} style={style} className={className}>
      {showDot && <BadgeDot />}
      {children}
    </StyledBadge>
  );
};

// ═══════════════════════════════════════════════════════════════
// STATUS BADGE (Preset for common statuses)
// ═══════════════════════════════════════════════════════════════

type StatusType = 'running' | 'completed' | 'failed' | 'pending' | 'paused';

const statusConfig: Record<StatusType, { variant: BadgeVariant; label: string }> = {
  running: { variant: 'green', label: 'Running' },
  completed: { variant: 'cyan', label: 'Completed' },
  failed: { variant: 'red', label: 'Failed' },
  pending: { variant: 'amber', label: 'Pending' },
  paused: { variant: 'default', label: 'Paused' },
};

interface StatusBadgeProps {
  status: StatusType;
  size?: BadgeSize;
}

export const StatusBadge: FC<StatusBadgeProps> = ({ status, size = 'md' }) => {
  const config = statusConfig[status];
  return (
    <Badge
      variant={config.variant}
      size={size}
      showDot
      animated={status === 'running'}
    >
      {config.label}
    </Badge>
  );
};

// ═══════════════════════════════════════════════════════════════
// COUNT BADGE (for showing numbers)
// ═══════════════════════════════════════════════════════════════

const CountBadgeStyled = styled.span<{ $variant: BadgeVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 var(--sk-space-2);
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-2xs);
  font-weight: 600;
  border-radius: var(--sk-radius-full);

  ${props => variantStyles[props.$variant]}
`;

interface CountBadgeProps {
  count: number;
  variant?: BadgeVariant;
  max?: number;
}

export const CountBadge: FC<CountBadgeProps> = ({
  count,
  variant = 'cyan',
  max = 99,
}) => {
  const displayCount = count > max ? `${max}+` : count.toString();
  return <CountBadgeStyled $variant={variant}>{displayCount}</CountBadgeStyled>;
};

export default Badge;
